import React, { useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { GiCheckMark } from "react-icons/gi";

interface Data {
  productId: number;
  defaultImageUrl: string;
  productName: string;
  sku: string;
  category: string;
  subCategory: string;
  quantityAvailable: number;
  price: number;
  oldPrice: number;
  status: string;
  createdAt: string;
}

interface ProductSearchProps {
  searchTerm: string;
  isOpen: boolean;
  isLoading: boolean;
  isError: boolean;
  data: Data[];
  isServiceTypeVendor: boolean;
  isProductSelected: (productId: number) => boolean;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onProductSelect: (product: Data, e: React.MouseEvent) => void;
  setIsOpen: (isOpen: boolean) => void;
}

const ProductSearch: React.FC<ProductSearchProps> = ({
  searchTerm,
  isOpen,
  isLoading,
  isError,
  data,
  isServiceTypeVendor,
  isProductSelected,
  onSearchChange,
  onFocus,
  onProductSelect,
  setIsOpen,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  const renderProductList = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-4 bg-white">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-customGreenColor"></div>
          <span className="ml-2">Loading products...</span>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="p-4 text-center text-red-500">
          Failed to load products. Please try again.
        </div>
      );
    }

    const filteredData = isServiceTypeVendor
      ? data
      : data.filter((product) => product.quantityAvailable > 0);

    return (
      <div className="bg-white">
        {filteredData.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No products found</div>
        ) : (
          filteredData.map((product) => (
            <div
              key={product.productId}
              className={`p-2 cursor-pointer ${
                isProductSelected(product.productId)
                  ? "bg-customGreenDisabled hover:bg-customGreenDisabled/80"
                  : "hover:bg-gray-200"
              } transition-colors`}
              onClick={(e) => onProductSelect(product, e)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {product.defaultImageUrl ? (
                    <img
                      src={product.defaultImageUrl}
                      alt={product.productName}
                      className="w-8 h-8 object-cover rounded mr-2"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "placeholder-image-url";
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-200 rounded mr-2 flex items-center justify-center">
                      <span className="text-xs text-gray-500">No img</span>
                    </div>
                  )}
                  <div>
                    <div className="font-medium">{product.productName}</div>
                    {!isServiceTypeVendor && (
                      <div className="text-sm text-gray-700">
                        Quantity: {product.quantityAvailable}
                      </div>
                    )}
                  </div>
                </div>
                {isProductSelected(product.productId) && (
                  <GiCheckMark className="w-5 h-5" />
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={inputRef}>
      <div className="relative text-sm w-full cursor-pointer">
        <input
          type="text"
          placeholder="Search products by name"
          value={searchTerm}
          onChange={onSearchChange}
          onFocus={onFocus}
          onClick={(e) => e.stopPropagation()}
          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-customGreenColor/50 focus:border-customGreenColor"
        />
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      {isOpen && (
        <div className="absolute w-full mt-1 bg-white shadow-lg rounded-lg z-50 max-h-[200px] overflow-y-auto">
          {renderProductList()}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
